import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiService, PollResponse, CarouselImage, ContestantDetail } from '../services/api.service';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Title, Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';

interface PollOption {
  value: string;
  label: string;
  votes: number;
  percentage?: number;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {

  pollId = '';
  pollCategoryId = '';
  pollQuestion = '';
  pollEnddatetime = '';
  pollOptions: PollOption[] = [];
  selectedPollOption = '';
  hasVoted = false;
  totalVotes = this.pollOptions.reduce((sum, option) => sum + option.votes, 0);
  isPollLoading = false;
  pollError = '';
  fingerprint = '';
  resultsFetched = false;
  introText = '';

  carouselSlides: CarouselImage[] = [];
  currentSlideIndex = 0;
  private autoSlideTimer: ReturnType<typeof setInterval> | null = null;

  newsVideoUrl: SafeResourceUrl | null = null;

  biggBossNews = [
    {
      title: 'Mid-Week Eviction Drama',
      time: '2 hrs ago',
      summary: 'Tension rises as housemates vote in a surprise open nomination process.'
    },
    {
      title: 'New Captain Takes Charge',
      time: '5 hrs ago',
      summary: 'Strenuous endurance task finishes with a new captain leading the house.'
    },
    {
      title: 'Weekend Special Preview',
      time: '1 day ago',
      summary: 'Host intervention expected following heated luxury budget arguments.'
    }
  ];

  contestants: ContestantDetail[] = [];

  constructor(
  private apiService: ApiService,
  private sanitizer: DomSanitizer,
  private titleService: Title,
  private metaService: Meta,
  private router: Router
  ) { }

  nextSlide(): void {
    if (!this.carouselSlides.length) {
      return;
    }
    this.currentSlideIndex = (this.currentSlideIndex + 1) % this.carouselSlides.length;
    this.resetAutoSlide();
  }

  prevSlide(): void {
    if (!this.carouselSlides.length) {
      return;
    }
    this.currentSlideIndex = (this.currentSlideIndex - 1 + this.carouselSlides.length) % this.carouselSlides.length;
    this.resetAutoSlide();
  }

  goToSlide(index: number): void {
    if (!this.carouselSlides.length) {
      return;
    }
    this.currentSlideIndex = index;
    this.resetAutoSlide();
  }

  startAutoSlide(): void {
    this.stopAutoSlide();
    if (!this.carouselSlides.length) {
      return;
    }
    this.autoSlideTimer = setInterval(() => {
      if (this.carouselSlides.length > 0) {
        this.currentSlideIndex = (this.currentSlideIndex + 1) % this.carouselSlides.length;
      }
    }, 5000);
  }

  stopAutoSlide(): void {
    if (this.autoSlideTimer) {
      clearInterval(this.autoSlideTimer);
      this.autoSlideTimer = null;
    }
  }

  resetAutoSlide(): void {
    this.startAutoSlide();
  }

  ngOnInit(): void {
    this.updateSeo();
    this.loadPoll();
    this.loadCarouselImages();
    this.loadConfiguration();
    this.loadContestants();
    this.initNewsVideo();
    this.initFingerprint();
  }
  updateSeo(): void {

  if (this.router.url === '/bigg-boss-tamil-vote') {

    this.titleService.setTitle(
      'Bigg Boss Tamil Season 10 Vote | CitySpeak'
    );

    this.metaService.updateTag({
      name: 'description',
      content:
        'Vote for your favourite Bigg Boss Tamil Season 10 contestants, check voting trends, contestant details and the latest Bigg Boss Tamil updates on CitySpeak.'
    });

    this.metaService.updateTag({
      name: 'robots',
      content: 'index, follow'
    });

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    canonical.setAttribute(
      'href',
      'https://www.cityspeak.in/bigg-boss-tamil-vote'
    );

  } else {

    this.titleService.setTitle(
      'CitySpeak – News, Polls, Bigg Boss Tamil & Public Opinion'
    );

    this.metaService.updateTag({
      name: 'description',
      content:
        'Stay informed, share your opinion and participate in trending polls on CitySpeak.'
    });

    this.metaService.updateTag({
      name: 'robots',
      content: 'index, follow'
    });

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    canonical.setAttribute(
      'href',
      'https://www.cityspeak.in/'
    );
  }
}

  initNewsVideo(): void {
    this.newsVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://www.youtube.com/embed/_PRbM3Q2y5k');
  }

  loadConfiguration(): void {
    this.apiService.getConfiguration('BB10_Header').subscribe({
      next: (configs) => {
        if (configs && configs.length > 0 && configs[0].configvalue) {
          this.introText = configs[0].configvalue;
        }
      },
      error: (err) => {
        console.error('Error fetching configuration:', err);
      }
    });
  }

  loadCarouselImages(): void {
    this.apiService.getCarouselImages('IC2600002').subscribe({
      next: (images) => {
        if (images && images.length > 0) {
          this.carouselSlides = images;
          this.currentSlideIndex = 0;
          this.startAutoSlide();
        }
      },
      error: (err) => {
        console.error('Error fetching carousel images:', err);
      }
    });
  }

  loadContestants(): void {
    this.apiService.getContestantDetails(10).subscribe({
      next: (data) => {
        if (data && Array.isArray(data)) {
          this.contestants = data;
        }
      },
      error: (err) => {
        console.error('Error fetching contestant details:', err);
      }
    });
  }

  getContestantStatusClass(position: string): string {
    if (!position) return 'status-safe';
    const p = position.trim().toLowerCase();
    if (p.includes('captain')) return 'status-captain';
    if (p.includes('nominate')) return 'status-nominated';
    if (p.includes('evict')) return 'status-evicted';
    if (p.includes('safe')) return 'status-safe';
    return 'status-safe';
  }

  ngOnDestroy(): void {
    this.stopAutoSlide();
  }

  loadPoll(): void {
    this.isPollLoading = true;
    this.pollError = '';

    this.apiService.getPoll().subscribe({
      next: (response) => {
        const pollQuestion = response.pollquestion && response.pollquestion[0];

        if (!pollQuestion) {
          this.pollError = 'No active poll found.';
          this.pollQuestion = '';
          this.pollOptions = [];
          return;
        }

        this.pollId = pollQuestion.dp_pollid;
        this.pollCategoryId = pollQuestion.dp_pnc_categoryid;
        this.pollQuestion = pollQuestion.dp_poll_question;
        this.pollEnddatetime = pollQuestion.dp_poll_enddatetime;
        this.pollOptions = response.options
          .filter((option) => option.dpo_pollid === this.pollId)
          .sort((first, second) => first.dpo_optionorder - second.dpo_optionorder)
          .map((option) => ({
            value: option.dpo_poll_optionid,
            label: option.dpo_options,
            votes: 0,
            percentage: 0
          }));
        this.selectedPollOption = '';
        this.hasVoted = false;
        this.totalVotes = 0;
        this.resultsFetched = false;

        if (this.pollEnddatetime) {
          const endDate = new Date(this.pollEnddatetime);
          if (!isNaN(endDate.getTime()) && new Date() >= endDate) {
            this.hasVoted = true;
            this.fetchPollResults();
          }
        }
        //this.checkIfAlreadyVoted();
      },
      error: () => {
        this.pollError = 'Unable to load poll.';
        this.pollQuestion = '';
        this.pollOptions = [];
        this.isPollLoading = false;
      },
      complete: () => {
        this.isPollLoading = false;
      }
    });
  }

  async initFingerprint(): Promise<void> {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.fingerprint = result.visitorId;
      //this.checkIfAlreadyVoted();
    } catch (error) {
      console.error('Error loading fingerprint:', error);
    }
  }

  checkIfAlreadyVoted(): void {
    if (!this.pollId) {
      return;
    }
    const votes = JSON.parse(localStorage.getItem('voted_polls') || '{}');
    const lastVoteDate = votes[this.pollId];
    if (lastVoteDate) {
      const today = new Date().toDateString();
      if (lastVoteDate === today) {
        this.hasVoted = true;
        this.fetchPollResults();
      }
    }
  }

  fetchPollResults(): void {
    if (this.resultsFetched) {
      return;
    }
    this.isPollLoading = true;
    this.apiService.getAlreadyVoted(this.pollId).subscribe({
      next: (response) => {
        let results: any[] = [];
        if (response && Array.isArray(response.value)) {
          results = response.value;
        } else if (Array.isArray(response)) {
          results = response;
        } else if (response && response.dpo_poll_optionid) {
          results = [response];
        }

        if (results.length > 0) {
          this.pollOptions.forEach((option) => {
            const result = results.find((r: any) => r.dpo_poll_optionid === option.value);
            if (result) {
              option.votes = result.dpo_noofpolls;
              option.percentage = result.dpo_pollpercent;
            }
          });
          this.pollOptions.sort((a, b) => b.votes - a.votes);
          this.totalVotes = this.pollOptions.reduce((sum, option) => sum + option.votes, 0);
          this.resultsFetched = true;
        }
      },
      error: (err) => {
        console.error('Error fetching poll results:', err);
        this.isPollLoading = false;
      },
      complete: () => {
        this.isPollLoading = false;
      }
    });
  }

  selectPollOption(optionValue: string): void {
    if (this.hasVoted) {
      return;
    }

    this.selectedPollOption = optionValue;
  }

  submitVote(): void {
    if (!this.selectedPollOption || this.hasVoted) {
      return;
    }
    debugger;
    const selectedOption = this.pollOptions.find((option) => option.value === this.selectedPollOption);

    if (!selectedOption) {
      return;
    }

    this.isPollLoading = true;
    this.pollError = '';

    this.apiService.submitVote(this.pollId, this.selectedPollOption, this.fingerprint).subscribe({
      next: (response) => {
        let results: any[] = [];
        if (response && Array.isArray(response.value)) {
          results = response.value;
        } else if (Array.isArray(response)) {
          results = response;
        } else if (response && response.dpo_poll_optionid) {
          results = [response];
        }

        if (results.length > 0) {
          this.pollOptions.forEach((option) => {
            const result = results.find((r: any) => r.dpo_poll_optionid === option.value);
            if (result) {
              option.votes = result.dpo_noofpolls;
              option.percentage = result.dpo_pollpercent;
            }
          });
        } else {
          // Fallback if no results matched format
          selectedOption.votes += 1;
        }

        this.pollOptions.sort((a, b) => b.votes - a.votes);
        this.totalVotes = this.pollOptions.reduce((sum, option) => sum + option.votes, 0);

        // Store vote in localStorage
        const votes = JSON.parse(localStorage.getItem('voted_polls') || '{}');
        votes[this.pollId] = new Date().toDateString();
        localStorage.setItem('voted_polls', JSON.stringify(votes));

        this.hasVoted = true;
        this.resultsFetched = true;
      },
      error: (err) => {
        this.pollError = err.error?.detail || 'Unable to submit vote.';
        this.isPollLoading = false;
      },
      complete: () => {
        this.isPollLoading = false;
      }
    });
  }

  getVotePercentage(option: PollOption): number {
    if (option.percentage !== undefined && option.percentage !== null) {
      return option.percentage;
    }
    if (!this.totalVotes) {
      return 0;
    }

    return (option.votes / this.totalVotes) * 100;
  }

  getFormattedPercentage(option: PollOption): string {
    return this.getVotePercentage(option).toFixed(2);
  }

  shareOnWhatsApp(): void {
    const pageUrl = (typeof window !== 'undefined' && window.location?.href) ? window.location.href : 'https://www.cityspeak.in/#opinion-poll';
    const shareText = `Vote for your favourite Bigg Boss Tamil Season 10 contestant on CitySpeak! 👇\n${pageUrl}`;
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, '_blank');
  }

  shareContestantsOnWhatsApp(): void {
    const contestantsUrl = 'https://www.cityspeak.in/#contestants';
    const shareText = `Check Bigg Boss Tamil Season 10 Contestants - Current Positions on CitySpeak! 👇\n${contestantsUrl}`;
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, '_blank');
  }

  shareContestants(): void {
    const contestantsUrl = 'https://www.cityspeak.in/#contestants';
    const shareData = {
      title: 'Bigg Boss Tamil Season 10 Contestants - Current Position | CitySpeak',
      text: 'Check out Bigg Boss Tamil Season 10 Contestants current positions and details on CitySpeak!',
      url: contestantsUrl
    };

    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share(shareData).catch((err) => {
        if (err && err.name !== 'AbortError') {
          console.log('Share error:', err);
        }
      });
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(contestantsUrl).then(() => {
        alert('Contestants link copied to clipboard: ' + contestantsUrl);
      }).catch(() => {
        prompt('Copy this link:', contestantsUrl);
      });
    }
  }

}
